import { CFDFile } from '@aero-platform/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useGenerateBlobSasUrlUpload } from '../api/upload-API'
import { listBlobsInFolder, startSimTemplateUpload } from '../services/blobUploadService'
import simUploadQueueStore from '../services/simUploadQueueStore'
import { UploadTypes } from '../utils/constants'
import { generateUniqueId } from '../utils/helpers'
import { verifySimTemplateLanded } from '../utils/simTemplateVerification'

/**
 * Lifecycle of a per-case `.sim` upload:
 *  - idle:      nothing uploaded in this session (also the state after a cancel)
 *  - uploading: transfer to Azure in flight
 *  - verifying: transfer finished, confirming the blob is listable in Azure
 *  - landed:    confirmed present in Azure — safe to submit/resubmit
 *  - failed:    transfer failed OR the blob could not be confirmed in Azure
 */
export type SimUploadPhase = 'idle' | 'uploading' | 'verifying' | 'landed' | 'failed'

export type UseSimTemplateUpload = {
  phase: SimUploadPhase
  progress: number
  error: string | null
  /** The template produced by the in-session upload (set as soon as the transfer starts). */
  uploadedTemplate: CFDFile | null
  /** True while the upload or verification is in flight — callers should disable submit. */
  isBusy: boolean
  /**
   * Begin a direct-to-blob upload, then await the transfer and verify the blob actually
   * landed in Azure. Resolves with the template once it is confirmed landed, or null on
   * failure (the phase/error are updated for the UI either way).
   */
  startUpload: (file: File, opts?: { runName?: string }) => Promise<CFDFile | null>
  /** Abort an in-flight transfer (e.g. the user closes/cancels the modal) and clear the banner. */
  cancel: () => void
  /** Reset back to idle (e.g. when a modal re-opens or the user picks an existing template). */
  reset: () => void
}

/**
 * Reusable tracker for per-case `.sim` uploads that gates submission on the blob actually
 * landing in Azure. Shared by ResubmitCaseModal, CaseEditor and CaseCreatorLine so all three
 * flows behave identically: no submit until the upload is verified present in storage.
 */
export function useSimTemplateUpload(): UseSimTemplateUpload {
  const [{}, generateSasUrlUpload] = useGenerateBlobSasUrlUpload()
  const [phase, setPhase] = useState<SimUploadPhase>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [uploadedTemplate, setUploadedTemplate] = useState<CFDFile | null>(null)
  const trackingIdRef = useRef<string>('')
  const abortRef = useRef<AbortController | null>(null)

  const reset = useCallback(() => {
    setPhase('idle')
    setProgress(0)
    setError(null)
    setUploadedTemplate(null)
    trackingIdRef.current = ''
    abortRef.current = null
  }, [])

  // Abort an in-flight transfer and drop its background-queue entry so the bottom banner stops
  // showing it. Safe to call when nothing is uploading (no-op). The aborted transfer rejects and
  // is handled as a clean cancel in startUpload's catch (no error toast).
  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    if (trackingIdRef.current) {
      simUploadQueueStore.dismiss(trackingIdRef.current)
      trackingIdRef.current = ''
    }
    setPhase('idle')
    setProgress(0)
    setError(null)
    setUploadedTemplate(null)
  }, [])

  // On unmount, abort any in-flight transfer and drop its queue entry. The owning row (e.g. a
  // CaseCreatorLine that was removed/reselected) is gone, so a still-running upload must not be
  // allowed to later publish a template or toast into a form that no longer exists. We touch refs
  // and the queue store only — not React state — because the component is being torn down; the
  // aborted controller makes startUpload's isCurrent() short-circuit without publishing.
  useEffect(
    () => () => {
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      if (trackingIdRef.current) {
        simUploadQueueStore.dismiss(trackingIdRef.current)
        trackingIdRef.current = ''
      }
    },
    []
  )

  const startUpload = useCallback(
    async (file: File, opts?: { runName?: string }): Promise<CFDFile | null> => {
      if (!/\.sim$/i.test(file.name)) {
        toast.error('Only .sim files are accepted.')
        return null
      }
      setError(null)
      setProgress(0)
      setPhase('uploading')
      const trackingId = `case-sim-${generateUniqueId()}`
      trackingIdRef.current = trackingId
      const controller = new AbortController()
      abortRef.current = controller
      // This upload is "current" only while it owns abortRef. A cancel() nulls abortRef and a
      // newer startUpload replaces it — in both cases this operation is stale and must NOT publish
      // success/failure or reset state, or it would clobber a newer upload / a cancelled case.
      const isCurrent = () => abortRef.current === controller
      try {
        const { directoryUrl, uploadPromise } = await startSimTemplateUpload(
          generateSasUrlUpload,
          UploadTypes.CASE_SIM_TEMPLATE_FILE,
          `${trackingId}/${file.name}`,
          file,
          (_path, percent) => {
            setProgress(percent)
            simUploadQueueStore.updateProgress(trackingId, percent)
          },
          controller.signal
        )
        if (!isCurrent()) return null

        const template = {
          system: 'starccm',
          file: file.name,
          path: directoryUrl,
          fileType: 'template',
          rootType: 'override'
        } as CFDFile
        setUploadedTemplate(template)

        // Keep the background banner in sync so a user who closes the modal still sees progress.
        simUploadQueueStore.registerSimple(
          { runId: trackingId, runName: opts?.runName || file.name, fileName: file.name },
          uploadPromise
        )

        // Block here until the transfer actually finishes — this is the key fix: the old code
        // returned as soon as the SAS URL was obtained, letting the user submit mid-transfer.
        await uploadPromise
        if (!isCurrent()) return null

        setPhase('verifying')
        const listBlobs = (uploadType: string, folderPath: string) =>
          listBlobsInFolder(generateSasUrlUpload, uploadType, folderPath)
        let landed = await verifySimTemplateLanded(template, listBlobs)
        if (!isCurrent()) return null
        if (!landed) {
          // Guard against a brief blob-list propagation delay: retry once before failing.
          await new Promise(resolve => setTimeout(resolve, 1500))
          if (!isCurrent()) return null
          landed = await verifySimTemplateLanded(template, listBlobs)
          if (!isCurrent()) return null
        }
        if (!landed) {
          setPhase('failed')
          setError('Upload finished but the SIM file could not be confirmed in Azure. Re-upload.')
          toast.error('SIM upload could not be confirmed in Azure — please re-upload.')
          return null
        }
        setPhase('landed')
        setProgress(100)
        return template
      } catch (err) {
        // If this operation was cancelled or superseded, leave all state to whoever owns it now.
        if (!isCurrent()) return null
        console.error('Per-case sim template upload failed:', err)
        setPhase('failed')
        setError(err instanceof Error ? err.message : 'Upload failed.')
        toast.error('Failed to upload SIM file.')
        return null
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
        }
      }
    },
    [generateSasUrlUpload]
  )

  return {
    phase,
    progress,
    error,
    uploadedTemplate,
    isBusy: phase === 'uploading' || phase === 'verifying',
    startUpload,
    cancel,
    reset
  }
}
